<script setup lang="ts">
import { computed, provide } from 'vue';
import PartialHeader from '@pkg/site-chrome/components/PartialHeader.vue';
import PartialFooter from '@pkg/site-chrome/components/PartialFooter.vue';
import DraftModeBar from '@pkg/site-chrome/components/DraftModeBar.vue';
import { languagePickerKey, type LanguagePickerApi } from '@pkg/site-chrome/injectionKeys';
import { appStoresKey } from '@pkg/modular-blocks/frontend/appStores';
import { useSanityDefaultSeoMeta } from '@pkg/sanity-utils/composables/useSanityDefaultSeoMeta';
import { businessPageSlugBySlug } from '~/apis/sanity/queries/pageBySlug';

const runtimeConfig = useRuntimeConfig();

/** Provide before any `await` so the header's picker can inject it. */
const languagePickerApi: LanguagePickerApi = {
  resolveLocaleSlug: async(locale, slugParts) => {
    if (!slugParts.slug || slugParts.slug === '/') {
      return null;
    }
    const result = await $fetch<{ page?: { slug?: string } | null }>(
      runtimeConfig.public.sanityFetchPath,
      {
        method: 'POST',
        body: {
          query: businessPageSlugBySlug(slugParts),
          params: { locale, ...slugParts },
        },
      },
    ).catch(() => null);
    return result?.page?.slug ?? null;
  },
};
provide(languagePickerKey, languagePickerApi);

const { data } = await useSiteManifestQuery();

if (data.value?.defaultSeo) {
  useSanityDefaultSeoMeta(data.value.defaultSeo);
}

provide(
  appStoresKey,
  computed(() => data.value?.appStores ?? null),
);
</script>

<template>
  <div class="sticky-footer">
    <div class="c-layout">
      <NuxtErrorBoundary>
        <PartialHeader
          v-if="data?.regionTheme?.mainNavigation"
          :menu-content="data.regionTheme.mainNavigation"
          :enable-location-search="false"
        />
      </NuxtErrorBoundary>

      <NuxtErrorBoundary>
        <main id="main" class="c-layout__main">
          <slot />
        </main>
      </NuxtErrorBoundary>
    </div>

    <NuxtErrorBoundary>
      <DraftModeBar />
      <PartialFooter
        v-if="data?.regionTheme?.mainFooter"
        variant="simple"
        :page-links="data.regionTheme.mainFooter.pageLinks"
        :social-links="data.regionTheme.mainFooter.socialLinks"
        :legal-links="data.regionTheme.mainFooter.legalLinks"
        :footnote="data.regionTheme.mainFooter.footnote ?? ''"
      />
    </NuxtErrorBoundary>
  </div>
</template>

<style scoped>
/* `.c-layout` page chrome (beige bg / z-index / min-height) lives in
   `assets/css/page.css.ts` — mirrors web layout + VE host styling. The footer
   sits outside `.c-layout` (as on web) so the page slides over its fixed back panel.
   Do not pad `main` for the fixed header: first `[data-theme]` section sits
   under the frosted nav (same as web). */
</style>
